import { useState, useEffect, useCallback } from "react";
import { toast } from "sonner";
import { Plus, Pencil, Power, Loader2, DoorOpen, Image as ImageIcon, X, Users, LayoutGrid } from "lucide-react";
import apiClient, { formatApiErrorDetail } from "@/lib/api";
import RoomForm from "@/components/property/RoomForm";
import PhotoManager from "@/components/property/PhotoManager";
import TenantManager from "@/components/property/TenantManager";
import FloorPlanEditor from "@/components/property/FloorPlanEditor";
import { AmenityIcon } from "@/components/property/amenityMeta";
import { ROOM_STATUS_LABELS, ROOM_STATUS_STYLES, formatRupiah } from "@/components/property/roomMeta";

export default function RoomsManager({ propertyId }) {
  const [rooms, setRooms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [photoRoom, setPhotoRoom] = useState(null);
  const [tenantRoom, setTenantRoom] = useState(null);
  const [showFloorPlan, setShowFloorPlan] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError(false);
    try {
      const { data } = await apiClient.get(`/properties/${propertyId}/rooms`);
      setRooms(data || []);
    } catch {
      setError(true);
    } finally {
      setLoading(false);
    }
  }, [propertyId]);

  useEffect(() => { load(); }, [load]);

  const openAdd = () => { setEditing(null); setDialogOpen(true); };
  const openEdit = (room) => { setEditing(room); setDialogOpen(true); };

  const deactivate = async (room) => {
    if (!window.confirm(`Nonaktifkan kamar ${room.room_number}?`)) return;
    try {
      await apiClient.delete(`/rooms/${room.id}`);
      toast.success("Kamar dinonaktifkan");
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    }
  };

  return (
    <div data-testid="rooms-manager">
      <div className="flex items-center justify-between gap-3 mb-4">
        <div>
          <h3 className="font-heading text-lg font-semibold">Daftar Kamar</h3>
          <p className="text-sm text-muted-foreground">Kelola kamar untuk property ini.</p>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <button type="button" onClick={() => setShowFloorPlan(true)} data-testid="room-floorplan-button"
            className="inline-flex items-center gap-2 px-4 py-2.5 rounded-full border border-border font-semibold text-primary hover:bg-secondary/60 transition-colors">
            <LayoutGrid size={16} /> Denah Kamar
          </button>
          <button type="button" onClick={openAdd} data-testid="room-add-button"
            className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-accent text-accent-foreground font-semibold hover:scale-105 transition-transform">
            <Plus size={16} /> Tambah Kamar
          </button>
        </div>
      </div>

      {loading ? (
        <div className="py-8 grid place-items-center"><Loader2 className="animate-spin text-primary" size={24} /></div>
      ) : error ? (
        <div className="rounded-xl border border-destructive/30 bg-destructive/10 p-4 text-sm text-destructive" data-testid="rooms-error">
          Gagal memuat daftar kamar.
        </div>
      ) : rooms.length === 0 ? (
        <div className="rounded-xl border border-dashed border-border bg-secondary/30 p-8 text-center" data-testid="rooms-empty">
          <DoorOpen size={26} className="mx-auto mb-2 text-primary" />
          <p className="text-sm text-muted-foreground">Belum ada kamar. Tambahkan kamar pertama Anda.</p>
        </div>
      ) : (
        <div className="space-y-3" data-testid="rooms-list">
          {rooms.map((r, i) => (
            <div key={r.id} data-testid={`room-item-${i}`}
              className="rounded-2xl border border-border bg-card p-4 flex flex-wrap items-start justify-between gap-4">
              <div className="min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="font-heading font-semibold" data-testid={`room-number-${i}`}>{r.room_number}</span>
                  {r.name && <span className="text-sm text-muted-foreground">· {r.name}</span>}
                  <span className={`text-xs font-semibold px-2.5 py-0.5 rounded-full border ${ROOM_STATUS_STYLES[r.status] || ""}`} data-testid={`room-status-${i}`}>
                    {ROOM_STATUS_LABELS[r.status] || r.status}
                  </span>
                </div>
                <div className="mt-1 flex flex-wrap gap-x-4 gap-y-1 text-sm text-muted-foreground">
                  <span data-testid={`room-price-${i}`}><span className="font-semibold text-primary">{formatRupiah(r.price_monthly)}</span> / bulan</span>
                  {r.size != null && <span data-testid={`room-size-${i}`}>{r.size} m²</span>}
                  {r.floor != null && <span>Lantai {r.floor}</span>}
                </div>
                {r.amenities?.length > 0 && (
                  <div className="mt-2 flex flex-wrap gap-1.5" data-testid={`room-amenities-${i}`}>
                    {r.amenities.map((a) => (
                      <span key={a.id} className="inline-flex items-center gap-1 rounded-full border border-border bg-secondary/40 px-2.5 py-1 text-xs">
                        <AmenityIcon name={a.icon} size={12} className="text-primary" /> {a.name}
                      </span>
                    ))}
                  </div>
                )}
              </div>
              <div className="flex items-center gap-2 shrink-0">
                <button type="button" onClick={() => setPhotoRoom(r)} data-testid={`room-photos-${i}`}
                  className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-full border border-border text-sm font-semibold text-primary hover:bg-secondary/60 transition-colors">
                  <ImageIcon size={14} /> Foto
                </button>
                <button type="button" onClick={() => setTenantRoom(r)} data-testid={`room-tenants-${i}`}
                  className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-full border border-border text-sm font-semibold text-primary hover:bg-secondary/60 transition-colors">
                  <Users size={14} /> Penghuni
                </button>
                <button type="button" onClick={() => openEdit(r)} data-testid={`room-edit-${i}`}
                  className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-full border border-border text-sm font-semibold text-primary hover:bg-secondary/60 transition-colors">
                  <Pencil size={14} /> Edit
                </button>
                {r.status !== "inactive" && (
                  <button type="button" onClick={() => deactivate(r)} data-testid={`room-deactivate-${i}`}
                    className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-full border border-border text-sm font-semibold text-destructive hover:bg-destructive/10 transition-colors">
                    <Power size={14} /> Nonaktifkan
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      <RoomForm
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        propertyId={propertyId}
        room={editing}
        onSaved={load}
      />

      {photoRoom && (
        <div className="fixed inset-0 z-[60] grid place-items-center p-4" data-testid="room-photo-modal">
          <div className="absolute inset-0 bg-foreground/40 backdrop-blur-sm" onClick={() => setPhotoRoom(null)} />
          <div className="relative w-full max-w-2xl bg-card border border-border rounded-2xl shadow-xl max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between px-6 py-4 border-b border-border sticky top-0 bg-card rounded-t-2xl">
              <h3 className="font-heading text-lg font-semibold">Foto Kamar {photoRoom.room_number}</h3>
              <button onClick={() => setPhotoRoom(null)} data-testid="room-photo-modal-close" className="p-1.5 rounded-lg hover:bg-secondary/60"><X size={18} /></button>
            </div>
            <div className="p-6">
              <PhotoManager propertyId={propertyId} roomId={photoRoom.id} title={`Foto Kamar ${photoRoom.room_number}`} />
            </div>
          </div>
        </div>
      )}

      {tenantRoom && (
        <div className="fixed inset-0 z-[60] grid place-items-center p-4" data-testid="room-tenant-modal">
          <div className="absolute inset-0 bg-foreground/40 backdrop-blur-sm" onClick={() => setTenantRoom(null)} />
          <div className="relative w-full max-w-2xl bg-card border border-border rounded-2xl shadow-xl max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between px-6 py-4 border-b border-border sticky top-0 bg-card rounded-t-2xl z-10">
              <h3 className="font-heading text-lg font-semibold">Penghuni — Kamar {tenantRoom.room_number}</h3>
              <button onClick={() => setTenantRoom(null)} data-testid="room-tenant-modal-close" className="p-1.5 rounded-lg hover:bg-secondary/60"><X size={18} /></button>
            </div>
            <div className="p-6">
              <TenantManager room={tenantRoom} />
            </div>
          </div>
        </div>
      )}

      {showFloorPlan && (
        <div className="fixed inset-0 z-[60] grid place-items-center p-4" data-testid="room-floorplan-modal">
          <div className="absolute inset-0 bg-foreground/40 backdrop-blur-sm" onClick={() => setShowFloorPlan(false)} />
          <div className="relative w-full max-w-6xl bg-card border border-border rounded-2xl shadow-xl max-h-[92vh] overflow-y-auto">
            <div className="flex items-center justify-between px-6 py-4 border-b border-border sticky top-0 bg-card rounded-t-2xl z-10">
              <h3 className="font-heading text-lg font-semibold">Denah Kamar — Editor</h3>
              <button onClick={() => setShowFloorPlan(false)} data-testid="room-floorplan-modal-close" className="p-1.5 rounded-lg hover:bg-secondary/60"><X size={18} /></button>
            </div>
            <div className="p-6">
              <FloorPlanEditor property={{ id: propertyId }} rooms={rooms} onClose={() => setShowFloorPlan(false)} />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
